import { Box, Typography, Grid } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from 'recharts';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

interface IntensityChartProps {
  data: any[];
  enableDarkTheme: boolean;
}

const IntensityChart = ({ data, enableDarkTheme }: IntensityChartProps) => {
  const { t } = useTranslation();
  const fontColor = enableDarkTheme ? '#fff' : '#000';

  const chartData =
    data?.map((d) => ({
      grade: d.road_grade,
      intensity: Number(d.avg_intensity),
    })) || [];

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: 0.5 }}>
        <Typography
          sx={{
            color: fontColor,
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'uppercase',
          }}
        >
          {t('frame_fatigue_intensity_by_road_grade')}
        </Typography>
        <InfoOutlinedIcon
          sx={{
            fontSize: '14px',
            color: enableDarkTheme ? '#8b8e98' : '#666',
          }}
        />
      </Box>
      <Typography
        sx={{
          fontSize: '11px',
          color: enableDarkTheme ? '#8b8e98' : '#333',
          mb: 2,
        }}
      >
        {t('frame_fatigue_hour_grade_type')}
      </Typography>

      <Box sx={{ flex: 1, minHeight: '150px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke={
                enableDarkTheme ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'
              }
            />
            <XAxis
              dataKey="grade"
              tick={{
                fill: enableDarkTheme ? '#8b8e98' : '#666',
                fontSize: 10,
              }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: '#8b8e98', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: enableDarkTheme ? '#1f1f1f' : '#fff',
                border: '1px solid #333',
                borderRadius: '4px',
              }}
              itemStyle={{ color: fontColor }}
            />
            <Bar dataKey="intensity" radius={[2, 2, 0, 0]} barSize={40}>
              {chartData.map((entry, index) => {
                const colors: any = {
                  Downhill: enableDarkTheme ? '#00f2ff' : '#409EFF',
                  Flat: '#E6A23C',
                  Uphill: '#F56C6C',
                };
                const grade =
                  String(entry.grade || '')
                    .charAt(0)
                    .toUpperCase() +
                  String(entry.grade || '')
                    .slice(1)
                    .toLowerCase();
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={colors[grade] || '#2196f3'}
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Grid container spacing={1} sx={{ mt: 2 }}>
        <Grid item xs={6}>
          <Box
            sx={{
              p: 1.5,
              backgroundColor: enableDarkTheme
                ? 'rgba(25, 28, 36, 0.5)'
                : '#f5f7fa',
              border: enableDarkTheme
                ? '1px solid rgba(255,255,255,0.05)'
                : '1px solid rgba(0,0,0,0.1)',
              borderRadius: '4px',
            }}
          >
            <Typography
              sx={{
                fontSize: '10px',
                color: enableDarkTheme ? '#2196f3' : '#1976d2',
                fontWeight: 600,
              }}
            >
              {t('normal_risk_levels')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: enableDarkTheme ? '#8b8e98' : '#333',
              }}
            >
              {t('4_765_m_highest_intensity_per_hour')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box
            sx={{
              p: 1.5,
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.2)',
              borderRadius: '4px',
            }}
          >
            <Typography
              sx={{ fontSize: '10px', color: '#f44336', fontWeight: 600 }}
            >
              {t('severe_high_exposure')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: enableDarkTheme ? '#8b8e98' : '#333',
              }}
            >
              {t('pure_mine_fatigue_longer_route_time')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default IntensityChart;
